import { useState, FormEvent } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { StockItem, MovementType } from '../../types';
import { recordMovement, RecordMovementRequest } from '../../services/storage';
import { ArrowUpDown, TrendingUp, TrendingDown } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

const MOVEMENT_TYPES: { value: MovementType; label: string; isEntry: boolean }[] = [
  { value: 'COMPRA', label: 'Compra (Entrada)', isEntry: true },
  { value: 'PRODUCAO_ENTRADA', label: 'Produção (Entrada)', isEntry: true },
  { value: 'DEVOLUCAO', label: 'Devolução (Entrada)', isEntry: true },
  { value: 'CONSUMO', label: 'Consumo (Saída)', isEntry: false },
  { value: 'VENDA', label: 'Venda (Saída)', isEntry: false },
  { value: 'GERACAO_RETALHO', label: 'Geração de Retalho', isEntry: false },
  { value: 'DESCARTE', label: 'Descarte (Saída)', isEntry: false },
  { value: 'AJUSTE', label: 'Ajuste de Inventário', isEntry: true },
];

interface RecordMovementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accessToken: string;
  selectedItem: StockItem | null;
  onSuccess: () => void;
}

export function RecordMovementDialog({
  open,
  onOpenChange,
  accessToken,
  selectedItem,
  onSuccess,
}: RecordMovementDialogProps) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [movementType, setMovementType] = useState<MovementType>('COMPRA');
  const [quantity, setQuantity] = useState('1');
  const [reason, setReason] = useState('');
  const [relatedOrderId, setRelatedOrderId] = useState('');
  const [fiscalDocId, setFiscalDocId] = useState('');

  const resetForm = () => {
    setMovementType('COMPRA');
    setQuantity('1');
    setReason('');
    setRelatedOrderId('');
    setFiscalDocId('');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedItem) return;

    setSaving(true);

    try {
      const selectedMovementType = MOVEMENT_TYPES.find(m => m.value === movementType);
      const isEntry = selectedMovementType?.isEntry ?? true;
      const quantityValue = parseInt(quantity);

      // For exits, we send negative quantity (backend might handle this differently)
      const request: RecordMovementRequest = {
        itemId: selectedItem.id,
        movementType,
        quantity: isEntry ? quantityValue : -quantityValue,
        reason: reason.trim() || undefined,
        relatedOrderId: relatedOrderId.trim() || undefined,
        fiscalDocId: fiscalDocId.trim() || undefined,
      };

      await recordMovement(accessToken, request);

      const actionLabel = isEntry ? 'Entrada' : 'Saída';
      toast.success(`${actionLabel} registrada com sucesso!`);
      resetForm();
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  const selectedMovementType = MOVEMENT_TYPES.find(m => m.value === movementType);
  const isEntry = selectedMovementType?.isEntry ?? true;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpDown className="h-5 w-5" />
            Registrar Movimentação
          </DialogTitle>
          <DialogDescription>
            {selectedItem ? (
              <>
                Item: <strong>{selectedItem.name}</strong>
                <br />
                Estoque atual: <strong>{selectedItem.quantity} {selectedItem.unit || 'un'}</strong>
              </>
            ) : (
              'Selecione um item para registrar movimentação'
            )}
          </DialogDescription>
        </DialogHeader>

        {selectedItem && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="movementType">Tipo de Movimentação *</Label>
              <Select value={movementType} onValueChange={(v) => setMovementType(v as MovementType)}>
                <SelectTrigger id="movementType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MOVEMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className="flex items-center gap-2">
                        {type.isEntry ? (
                          <TrendingUp className="h-4 w-4 text-green-500" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-500" />
                        )}
                        {type.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantity">
                Quantidade *
                <span className={`ml-2 text-xs ${isEntry ? 'text-green-600' : 'text-red-600'}`}>
                  ({isEntry ? '+' : '-'} entrada/saída)
                </span>
              </Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                required
              />
              {!isEntry && parseInt(quantity) > selectedItem.quantity && (
                <p className="text-xs text-destructive">
                  Quantidade maior que o estoque disponível ({selectedItem.quantity})
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Motivo / Observações</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Descreva o motivo da movimentação..."
                rows={2}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="relatedOrderId">Pedido Relacionado</Label>
                <Input
                  id="relatedOrderId"
                  value={relatedOrderId}
                  onChange={(e) => setRelatedOrderId(e.target.value)}
                  placeholder="Ex: MT-2026-0001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fiscalDocId">Nota Fiscal</Label>
                <Input
                  id="fiscalDocId"
                  value={fiscalDocId}
                  onChange={(e) => setFiscalDocId(e.target.value)}
                  placeholder="Ex: NF-123456"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving || (!isEntry && parseInt(quantity) > selectedItem.quantity)}
                className={isEntry ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {saving ? 'Salvando...' : isEntry ? 'Registrar Entrada' : 'Registrar Saída'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
